import React, { useState, useEffect } from 'react';

// Material UI
import TextField from '@mui/material/TextField';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select, { SelectChangeEvent } from '@mui/material/Select';

// Material UI Icons
import SettingsBackupRestoreIcon from '@mui/icons-material/SettingsBackupRestore';
import SaveIcon from '@mui/icons-material/Save';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';

// Componentes 

// Funciones para obtener datos
import ObtenerPais from '../functions/ObtenerPais';
import GuardarPais from '../functions/GuardarPais';
import InsertarPais from '../functions/InsertarPais';

// Tipos de datos
import PaisEditar from '../types/PaisEditar';

// Indicador de procesamiento
import CircularProgress from '@mui/material/CircularProgress';

interface EditarPaisProps {
    idPais: number;
    guardarCambios: (pais: PaisEditar) => void;
    cancelarCambios: () => void;
    eliminandoPais: boolean;
}

export const EditarPais: React.FC<EditarPaisProps> = ({ idPais, guardarCambios, cancelarCambios, eliminandoPais = false }) => {
  
    const [isLoading, setIsLoading] = useState(true);
    const [pais, setPais] = useState<PaisEditar | null>(null);
    const [errorPais, setErrorPais] = useState<string | null>(null);

    const [localNombre, setLocalNombre] = useState('');
    const [localCodigoIso, setLocalCodigoIso] = useState('');
    const [localCodigoTel, setLocalCodigoTel] = useState('');
    const [localFchCrea, setLocalFchCrea] = useState('');
    const [localFchModi, setLocalFchModi] = useState('');
    const [localFchBaja, setLocalFchBaja] = useState('');
    const [localHabilitado, setLocalHabilitado] = useState('');
    const [localDadoBaja, setLocalDadoBaja] = useState('');
    const [localIdPais, setLocalIdPais] = useState(0);


    useEffect(() => {
        
        const fetchPais = async () => {
            try {
                const data = await ObtenerPais(idPais);
                if (data.pais_ok === 'si') {

                    // Todo ok, pais recuperado
                    const paisRecuperado: PaisEditar = {
                        id: data.pais[0].id,
                        pais: data.pais[0].nombre,
                        codigo_iso: data.pais[0].codigo_iso,
                        codigo_tel: data.pais[0].codigo_telefonico,
                        habilitado: data.pais[0].habilitado,
                        fch_crea: data.pais[0].fecha_creacion,
                        fch_modi: data.pais[0].fecha_modificacion,
                        dado_baja: data.pais[0].dado_de_baja,
                        fch_baja: data.pais[0].fecha_baja
                    };

                    setPais(paisRecuperado);

                    setLocalNombre(data.pais[0].nombre);
                    setLocalCodigoIso(data.pais[0].codigo_iso);
                    setLocalCodigoTel(data.pais[0].codigo_telefonico);
                    setLocalHabilitado(data.pais[0].habilitado);
                    setLocalDadoBaja(data.pais[0].dado_de_baja);
                    setLocalFchCrea(data.pais[0].fecha_creacion);

                    setErrorPais(null);
                } else {
                    
                    // Hubo errores al recuperar el pais
                    setErrorPais(data.mensaje);
                    setPais(null);
                    setLocalNombre('');
                    setLocalCodigoIso('');
                    setLocalCodigoTel('');
                    setLocalFchCrea('');
                    setLocalFchModi('');
                    setLocalFchBaja('');
                    setLocalHabilitado('');
                    setLocalDadoBaja('');

                }
                setIsLoading(false);
                
            } catch (err) {
                setErrorPais('Error al obtener la información del pais.');
                setIsLoading(false);
                setPais(null);
                setLocalNombre('');
                setLocalCodigoIso('');
                setLocalCodigoTel('');
                setLocalFchCrea('');
                setLocalFchModi('');
                setLocalFchBaja('');
                setLocalHabilitado('');
                setLocalDadoBaja('');
            }
        };

        if (idPais > 0) {
            // Estoy editando un pais
            fetchPais();

        } else {
            // Estoy creando un nuevo pais
            setIsLoading(false);
            setPais({pais: '', codigo_iso: '', codigo_tel: '', habilitado: 'S', fch_crea: '', fch_modi: '', dado_baja: 'N', fch_baja: '', id: 0});
            setLocalHabilitado('S');
            setLocalDadoBaja('N');
        }

    }, []);

    const handleChangeLocalHabitado = (event: SelectChangeEvent) => {
        let valor = event.target.value;
        let valorMayuscula = valor.toUpperCase();
        setLocalHabilitado(valorMayuscula);
    };

    const handleChangeLocalDadoBaja = (event: SelectChangeEvent) => {
        let valor = event.target.value;
        let valorMayuscula = valor.toUpperCase();
        setLocalDadoBaja(valorMayuscula);
    };

    const handleChangeLocalCodigoIso = (event: React.ChangeEvent<HTMLInputElement>) => {
        let valor = event.target.value;
        let valorMayuscula = valor.toUpperCase();
        if (valorMayuscula.length <= 2) {
            setLocalCodigoIso(valorMayuscula);
        }
    };

    const handleChangeLocalCodigoTel = (event: React.ChangeEvent<HTMLInputElement>) => {
        const pattern = /^(\d{0,1}\s?\d{0,3}|\d{1,4})$/
        let valor = event.target.value;
        let valorMayuscula = valor.toUpperCase();
        if (valorMayuscula.length <= 5) {
            if (pattern.test(valorMayuscula)) {
                setLocalCodigoTel(valorMayuscula);
            }
        }
    };

    const guardarCambiosLocal = async () => {

        if (idPais === 0) {
            // Crear nuevo pais

            const PaisNuevo: PaisEditar = {
                id: 0,
                pais: localNombre,
                codigo_iso: localCodigoIso,
                codigo_tel: localCodigoTel,
                habilitado: localHabilitado,
                fch_crea: '',
                fch_modi: '',
                dado_baja: localDadoBaja,
                fch_baja: ''
            };

            const respuesta = await InsertarPais(PaisNuevo);

            if (respuesta.pais_guardado === 'si') {
                guardarCambios(PaisNuevo);
            } else {
                alert('Error al guardar los cambios: ' + respuesta.mensaje);
            }
            
        } else {

            if (eliminandoPais) {
                
                // Se realiza la baja lógica del pais
                // HABILITADO = N
                // DADO_BAJA = S
                const fechaActual = new Date().toISOString();
                const guardarFecha = fechaActual.slice(0, 10)+" "+fechaActual.slice(11, 19);

                // Modificar datos pais
                const PaisEditado: PaisEditar = {
                    id: idPais,
                    pais: localNombre,
                    codigo_iso: localCodigoIso,
                    codigo_tel: localCodigoTel,
                    habilitado: 'N',
                    fch_crea: localFchCrea,
                    fch_modi: guardarFecha,
                    dado_baja: 'S',
                    fch_baja: guardarFecha
                };

                const respuesta = await GuardarPais(PaisEditado);

                if (respuesta.pais_guardado === 'si') {
                    guardarCambios(PaisEditado);
                } else {
                    alert('Error al guardar los cambios: ' + respuesta.mensaje);
                }
                
            } else {
                
                // Modificar pais
                const fechaActual = new Date().toISOString();
                const guardarFecha = fechaActual.slice(0, 10)+" "+fechaActual.slice(11, 19);
                // Modificar datos pais
                const PaisEditado: PaisEditar = {
                    id: idPais,
                    pais: localNombre,
                    codigo_iso: localCodigoIso,
                    codigo_tel: localCodigoTel,
                    habilitado: localHabilitado,
                    fch_crea: localFchCrea,
                    fch_modi: guardarFecha,
                    dado_baja: localDadoBaja,
                    fch_baja: localDadoBaja === 'S' ? guardarFecha : ''
                };

                const respuesta = await GuardarPais(PaisEditado);

                if (respuesta.pais_guardado === 'si') {
                    guardarCambios(PaisEditado);
                } else {
                    alert('Error al guardar los cambios');
                }
            }
        }
    };

    const handleChangeLocalNombre = (event: React.ChangeEvent<HTMLInputElement>) => {
        setLocalNombre(event.target.value);
    };

    return (
        <>
            {isLoading && (
                <div>
                    <p className="text-lg">Por favor espere</p>
                    <CircularProgress />
                </div>
            )}
            {(!isLoading && pais) && (
                <div className="container mx-auto px-4 sm:px-6 lg:px-8 mt-5">
                    {eliminandoPais ? (
                        <h1 className="font-metropolis text-xl text-red-700 font-bold mb-2">Eliminando Pais: {pais?.pais}</h1>
                    ) : (
                        <h1 className="font-metropolis text-xl font-bold mb-2">{idPais > 0 ? "Editando Pais: " + pais?.pais : "Nuevo Pais"}</h1>
                    )}     
                    <div className="border-t-2 border-b-2 border-vp-azul w-full flex flex-row pt-3 flex-wrap justify-between items-start">
                        <div className="flex flex-col justify-start items-center max-w-[320px] flex-grow w-full">
                            <TextField id="nombre" label="Pais" variant="outlined" value={localNombre} autoFocus
                                sx={{
                                    '& .MuiOutlinedInput-input': {
                                        padding: '8px',
                                    },
                                    '& .MuiInputLabel-outlined': {
                                        top: '-6px',
                                    },
                                    '& .MuiInputLabel-shrink': {
                                        top: '0px',
                                    },
                                    width: '100%',
                                    marginRight: '6px',
                                    marginBottom: '8px',
                                }}
                                onChange={handleChangeLocalNombre}
                                disabled={eliminandoPais}
                            />
                        </div>
                        <div className="flex flex-col justify-between items-center max-w-[100px] flex-grow w-full">
                            <TextField id="codigo_iso" label="Cód.ISO" variant="outlined" value={localCodigoIso}
                                sx={{
                                    '& .MuiOutlinedInput-input': {
                                        padding: '8px',
                                    },
                                    '& .MuiInputLabel-outlined': {
                                        top: '-6px',
                                    },
                                    '& .MuiInputLabel-shrink': {
                                        top: '0px',
                                    },
                                    width: '100%',
                                    marginRight: '6px',
                                    marginBottom: '8px',
                                }}
                                onChange={handleChangeLocalCodigoIso}
                                disabled={eliminandoPais}
                            />
                        </div>
                        <div className="flex flex-col justify-between items-center max-w-[100px] flex-grow w-full">
                            <TextField id="codigo_tel" label="Cód.TEL" variant="outlined" value={localCodigoTel}
                                sx={{
                                    '& .MuiOutlinedInput-input': {
                                        padding: '8px',
                                    },
                                    '& .MuiInputLabel-outlined': {
                                        top: '-6px',
                                    },
                                    '& .MuiInputLabel-shrink': {
                                        top: '0px',
                                    },
                                    width: '100%',
                                    marginRight: '6px',
                                    marginBottom: '8px',
                                }}
                                onChange={handleChangeLocalCodigoTel}
                                disabled={eliminandoPais}
                            />
                        </div>
                        {pais.id > 0 && (
                            <div className="flex flex-col justify-between items-center max-w-[170px] flex-grow w-full">
                                <TextField id="fchcrea" label="Fec. Creado" variant="outlined" value={localFchCrea}
                                    sx={{
                                        '& .MuiOutlinedInput-input': {
                                            padding: '8px',
                                        },
                                        '& .MuiInputLabel-outlined': {
                                            top: '-6px',
                                        },
                                        '& .MuiInputLabel-shrink': {
                                            top: '0px',
                                        },
                                        width: '100%',
                                        marginRight: '6px',
                                        marginBottom: '8px',
                                    }}
                                    disabled
                                />
                            </div>)
                        }
                        <div className="flex flex-col justify-between items-center max-w-[120px] flex-grow w-full">
                            <FormControl fullWidth sx={{marginRight: '6px',}}>
                                <InputLabel id="label-habilitado"
                                    sx={{top: '-6px',
                                        '&.MuiInputLabel-shrink': {
                                            transform: 'translate(14px, 0px) scale(0.75)',
                                        },
                                    }}
                                    >
                                        Habilitado
                                    </InputLabel>
                                <Select
                                    labelId="label-habilitado"
                                    id="habilitado"
                                    value={localHabilitado}
                                    label="Habilitado"
                                    sx={{
                                        '& .MuiOutlinedInput-input': {
                                            padding: '8px',
                                        },
                                        width: '100%',
                                        marginRight: '6px',
                                        marginBottom: '8px',
                                    }}
                                    onChange={handleChangeLocalHabitado}
                                    disabled={eliminandoPais}
                                    >
                                    <MenuItem value={'S'}>Si</MenuItem>
                                    <MenuItem value={'N'}>No</MenuItem>
                                </Select>
                            </FormControl> 
                        </div>
                        <div className="flex flex-col justify-between items-center max-w-[120px] flex-grow w-full">
                            <FormControl fullWidth sx={{marginRight: '6px',}}>
                                <InputLabel id="label-dadobaja"
                                    sx={{top: '-6px',
                                        '&.MuiInputLabel-shrink': {
                                            transform: 'translate(14px, 0px) scale(0.75)',
                                        },
                                    }}
                                    >
                                        Baja
                                    </InputLabel>
                                <Select
                                    labelId="label-dadobaja"
                                    id="dadobaja"
                                    value={localDadoBaja}
                                    label="Baja"
                                    sx={{
                                        '& .MuiOutlinedInput-input': {
                                            padding: '8px',
                                        },
                                        width: '100%',
                                        marginRight: '6px',
                                        marginBottom: '8px',
                                    }}
                                    onChange={handleChangeLocalDadoBaja}
                                    disabled={eliminandoPais}
                                    >
                                    <MenuItem value={'S'}>Si</MenuItem>
                                    <MenuItem value={'N'}>No</MenuItem>
                                </Select>
                            </FormControl> 
                        </div>
                    </div>
                    <div className="flex flex-row justify-end items-center space-x-2">
                        {eliminandoPais ? (
                            <>
                                <button className="mt-3 vp-btn-rojo-sm" onClick={guardarCambiosLocal} ><DeleteForeverIcon /> Eliminar</button>
                                <button className="mt-3 vp-btn-azul-sm" onClick={cancelarCambios} ><SettingsBackupRestoreIcon /> Cancelar</button>
                            </>
                        ) : (
                            <>
                                <button className="mt-3 vp-btn-azul-sm" onClick={guardarCambiosLocal} ><SaveIcon /> Guardar Cambios</button>
                                <button className="mt-3 vp-btn-rojo-sm" onClick={cancelarCambios} ><SettingsBackupRestoreIcon /> Cancelar</button>
                            </>
                        )}
                        
                    </div>
                </div>
            )}
        </>
    );
};

// MuiInputLabel-formControl MuiInputLabel-animated MuiInputLabel-shrink MuiInputLabel-sizeMedium MuiInputLabel-outlined css-xo08j0-MuiFormLabel-root-MuiInputLabel-root